import { ValueOf } from "../../../pojo/Util";
import { Report as Reports } from "../../../pojo/Report";

export namespace Doc {
    export type Type = ValueOf<typeof type>;
    export type TypeId = Type["id"];
    export type TypeReportId = Type["reportId"];
    export type Scope = "subreport";

    export const type = {
        event: {
            id: "event",
            label: "Event",
            order: 0,
            reportId: Reports.Reports.EventShell.id,
            type: "standard",
            category: "Object Based",
        },
        reservation: {
            id: "reservation",
            label: "Occurrence",
            order: 1,
            reportId: Reports.Reports.ReservationShell.id,
            type: "standard",
            category: "Object Based",
        },
        reservations: {
            id: "reservations",
            label: "Occurrences",
            order: 1.1,
            reportId: Reports.Reports.ReservationsShell.id,
            type: "reportBuilder",
            category: "Search Based",
        },
        organization: {
            id: "organization",
            label: "Organization",
            order: 2,
            reportId: Reports.Reports.OrganizationShell.id,
            type: "standard",
            category: "Object Based",
        },
        invoice: {
            id: "invoice",
            label: "Invoice (Payment)",
            order: 2.1,
            reportId: Reports.Reports.PaymentShell.id,
            type: "standard",
            category: "Object Based",
        },
        eventListing: {
            id: "eventListing",
            label: "Event Listing",
            order: 3,
            reportId: Reports.Reports.EventListingShell.id,
            type: "tableBuilder",
            tableBuilder: [{ id: "data", label: "Data", key: "eventListing", variable: "ws.flexTable.events" }],
            category: "Excel",
        },
        rsrvListing: {
            id: "rsrvListing",
            label: "Occurrences By Date",
            order: 4,
            reportId: Reports.Reports.ReservationListingShell.id,
            type: "tableBuilder",
            tableBuilder: [
                { id: "data", label: "Data", key: "reservationListing", variable: "ws.flexTable.rsrvsByDate" },
            ],
            category: "Excel",
        },
        locListing: {
            id: "locListing",
            label: "Location Listing",
            order: 5,
            reportId: Reports.Reports.LocationListingShell.id,
            type: "tableBuilder",
            tableBuilder: [{ id: "data", label: "Data", key: "locationListing", variable: "ws.flexTable.locations" }],
            category: "Excel",
        },
        resListing: {
            id: "resListing",
            label: "Resource Listing",
            order: 6,
            reportId: Reports.Reports.ResourceListingShell.id,
            type: "tableBuilder",
            tableBuilder: [{ id: "data", label: "Data", key: "resourceListing", variable: "ws.flexTable.resources" }],
            category: "Excel",
        },
        orgListing: {
            id: "orgListing",
            label: "Organization Listing",
            order: 7,
            reportId: Reports.Reports.OrganizationListingShell.id,
            type: "tableBuilder",
            tableBuilder: [
                { id: "data", label: "Data", key: "organizationListing", variable: "ws.flexTable.organizations" },
            ],
            category: "Excel",
        },
        invoiceExcel: {
            id: "invoiceExcel",
            label: "Invoice Details (Payment)",
            order: 8,
            reportId: Reports.Reports.InvoiceShell.id,
            type: "tableBuilder",
            tableBuilder: [
                {
                    id: "summary",
                    label: "Summary",
                    key: "invoicePaymentSummary",
                    variable: "ws.flexTable.invoicePaymentSummary",
                },
                {
                    id: "details",
                    label: "Details",
                    key: "invoicePaymentDetails",
                    variable: "ws.flexTable.invoicePaymentDetails",
                },
            ],
            category: "Excel",
        },
        rsrvReq: {
            id: "rsrvReq",
            label: "Requirements",
            order: 1,
            type: "subreport",
            reportId: null as number,
        },
        rsrvEventCustAtrb: {
            id: "rsrvEventCustAtrb",
            label: "Custom Attributes",
            order: 2,
            type: "subreport",
            reportId: null as number,
        },
        rsrvLoc: {
            id: "rsrvLoc",
            label: "Locations",
            order: 3,
            type: "subreport",
            reportId: null as number,
        },
        rsrvRsrc: {
            id: "rsrvRsrc",
            label: "Resources",
            order: 4,
            type: "subreport",
            reportId: null as number,
        },
        rsrvRsrcByCat: {
            id: "rsrvRsrcByCat",
            label: "Resources By Category",
            order: 5,
            type: "subreport",
            reportId: null as number,
        },
        rsrvOrg: {
            id: "rsrvOrg",
            label: "Organizations",
            order: 6,
            type: "subreport",
            reportId: null as number,
        },
        rsrvContact: {
            id: "rsrvContact",
            label: "Contacts",
            order: 7,
            type: "subreport",
            reportId: null as number,
        },
        rsrvRsrcPref: {
            id: "rsrvRsrcPref",
            label: "Resource Preferences",
            order: 8,
            type: "subreport",
            reportId: null as number,
        },
        rsrvLocPref: {
            id: "rsrvLocPref",
            label: "Location Preferences",
            order: 9,
            type: "subreport",
            reportId: null as number,
        },
    } as const;

    export const allTypes = Object.values(type).sort((a, b) => a.order - b.order);
    export const defaultTypes = allTypes.filter((type) => type.type !== "subreport");
    export const subreportTypes = allTypes.filter((type) => type.type === "subreport");

    export type TypeOptions = ValueOf<typeof type>[];
    export type ReportBuilderTypeId = ValueOf<{
        [P in keyof typeof type]: (typeof type)[P] extends { type: "reportBuilder" | "subreport" } ? P : never;
    }>;
    export type SubreportTypeId = ValueOf<{
        [P in keyof typeof type]: (typeof type)[P] extends { type: "subreport" } ? P : never;
    }>;

    export const typeById = new Map(Object.values(type).map((type) => [type.id, type]));

    // Parses the columns of a table builder table variable
    // Format: {{for: <table variable> : <column1> || <column2> || ... :end-for}}
    // Columns: <variable> | <format> : <header> : <width>
    // Where only variable and header are required
    export const tableBuilderColumnRegex =
        /(?<variable>[^:|]+)\s*(?:\|\s*(?<format>[^:|]+)\s*)?:\s*(?<header>[^:|]+)\s*(?::\s*(?<width>\d+)\s*)?\s*(?:\|\||:end-for)/gi;

    export const tableBuilderRegex = /(?<table>\{\{for:\s*(?<variable>[$.\w\d]+):\s*(?<columns>.*?)\s*:end-for}})/gi;

    // Parses the columns of a complex table builder table variable
    // Format: {{for: <table variable> : <column1> || <column2> || ... :end-for}}
    // Columns: <report section> ^ <row number> ^ <label> ^ <variable> | <format> ^ <style> ^ <width>
    export const reportBuilderColumnRegex =
        /(?<section>[\w_]+)\s*\^\s*(?<row>\d+)\s*\^\s*(?<label>[^\^]+?)?\s*\^\s*(?<variable>[^\^]+?)?\s*\^\s*(?<style>[^\^]+?)?\s*\^\s*(?<width>[\d.]+)\s*(?:\|\||:end-for)/gi;

    export const supportedCssProperties = [
        "background",
        "background-attachment",
        "background-color",
        "background-image",
        "background-position",
        "background-repeat",
        "border",
        "border-bottom",
        "border-bottom-color",
        "border-bottom-style",
        "border-bottom-width",
        "border-collapse",
        "border-color",
        "border-left",
        "border-left-color",
        "border-left-style",
        "border-left-width",
        "border-right",
        "border-right-color",
        "border-right-style",
        "border-right-width",
        "border-spacing",
        "border-style",
        "border-top",
        "border-top-color",
        "border-top-style",
        "border-top-width",
        "border-width",
        "bottom",
        "caption-side",
        "clear",
        "clip",
        "color",
        "content",
        "counter-increment",
        "counter-reset",
        "direction",
        "display",
        "empty-cells",
        "float",
        "fonts",
        "font-family",
        "font-size",
        "font-size-adjust",
        "font-stretch",
        "font-style",
        "font-variant",
        "font-weight",
        "height",
        "left",
        "letter-spacing",
        "line-height",
        "list-style",
        "list-style-position",
        "list-style-type",
        "margin",
        "margin-bottom",
        "margin-left",
        "margin-right",
        "margin-top",
        "marker-offset",
        "max-height",
        "max-width",
        "min-height",
        "min-width",
        "orphans",
        "overflow",
        "padding",
        "padding-bottom",
        "padding-left",
        "padding-right",
        "padding-top",
        "page",
        "page-break-after",
        "page-break-before",
        "page-break-inside",
        "position",
        "quotes",
        "right",
        "size",
        "table-layout",
        "text-align",
        "text-decoration",
        "text-indent",
        "text-transform",
        "top",
        "unicode-bibi",
        "vertical-align",
        "visibility",
        "white-space",
        "widows",
        "width",
        "word-spacing",
        "z-index",
    ];

    export const reportBuilderFunctions = new Set(["concat"]);

    export const reportBuilderDefaultSort = {
        reservations: ["reservations_start_dt", "reservation_end_dt", "event_name"],
        rsrvReq: ["requirement_name", "requirement_type_name", "requirement_count"],
        rsrvEventCustAtrb: ["attribute_name", "attribute_type_name", "attribute_value"],
        rsrvLoc: ["space_name", "max_capacity", "none"],
        rsrvRsrc: ["resource_name", "resource_count", "none"],
        rsrvRsrcByCat: ["resource_name", "resource_count", "none"],
        rsrvOrg: ["organization_name", "organization_type_name", "none"],
        rsrvContact: ["role_name", "contact_name", "none"],
        rsrvRsrcPref: ["pref_resource_name", "pref_resource_quantity", "none"],
        rsrvLocPref: ["pref_space_name", "pref_max_capacity", "none"],
    };

    export const pageOrientationOptions = [
        { val: "portrait", txt: "Portrait" },
        { val: "landscape", txt: "Landscape" },
    ];
    export const pageOrientation = Object.fromEntries(pageOrientationOptions.map((option) => [option.val, option.txt]));

    export const reportBuilderGroupByOptions = [
        { val: "none", txt: "None" },
        { val: "date", txt: "By Date" },
        { val: "dateReservation", txt: "By Date & Occurrence" },
        { val: "weeklyCalendar", txt: "Weekly Calendar" },
        { val: "monthlyCalendar", txt: "Monthly Calendar" },
        { val: "monthlyCalendarContinuous", txt: "Monthly Calendar Continuous" },
    ];
    export const reportBuilderGroupBy = Object.fromEntries(
        reportBuilderGroupByOptions.map((option) => [option.val, option.txt]),
    );

    export const reportBuilderPageBreakOptions = [
        { val: "none", txt: "None" },
        { val: "group", txt: "By Group" },
        { val: "reservation", txt: "By Occurrence" },
    ];
    export const reportBuilderPageBreak = Object.fromEntries(
        reportBuilderPageBreakOptions.map((option) => [option.val, option.txt]),
    );
}
